import { useMemo, useState } from "react";
import { updateList } from "@/mocks/updates";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import UpdateCard from "@/components/feature/UpdateCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";

const PAGE_SIZE = 4;

export default function UpdatesPage() {
  useDocumentTitle(
    "动画更新列表 - 漫蛙2动漫资讯平台",
    "漫蛙2动画更新列表，按最新更新时间整理在番作品、集数进度与剧情简介，追番不漏更。"
  );
  const [genre, setGenre] = useState("全部");
  const [sort, setSort] = useState("episode");
  const [visible, setVisible] = useState(PAGE_SIZE);

  const genres = useMemo(() => ["全部", ...Array.from(new Set(updateList.flatMap((u) => u.genre)))], []);

  const filtered = useMemo(() => {
    const list = updateList.filter((u) => genre === "全部" || u.genre.includes(genre));
    const sorted = [...list].sort((a, b) => (sort === "title" ? a.title.localeCompare(b.title) : b.episode / b.totalEpisodes - a.episode / a.totalEpisodes));
    return sorted;
  }, [genre, sort]);

  const shown = filtered.slice(0, visible);

  return (
    <div className="pb-4">
      <section className="border-b border-background-200 bg-background-100">
        <div className="page-wrap py-8 md:py-12">
          <Breadcrumb items={[{ label: "动画更新" }]} />
          <h1 className="mt-4 text-2xl font-extrabold text-foreground-950 md:text-3xl">动画更新</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            按最新更新时间整理的在番列表，包含集数、更新星期与剧情简介。点击作品查看完整剧集信息。
          </p>
        </div>
      </section>

      <div className="page-wrap mt-8">
        <div className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            {genres.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => {
                  setGenre(item);
                  setVisible(PAGE_SIZE);
                }}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                  genre === item
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:text-primary-600"
                }`}
              >
                {item}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <span className="whitespace-nowrap text-xs text-foreground-500">排序</span>
            <div className="flex rounded-full bg-background-100 p-1">
              {[
                { key: "episode", label: "更新进度" },
                { key: "title", label: "名称排序" },
              ].map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setSort(option.key)}
                  className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                    sort === option.key ? "bg-background-50 text-primary-700" : "text-foreground-500"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {shown.length === 0 ? (
          <div className="mt-8">
            <EmptyState
              title="该分类下暂无更新作品"
              description="换个类型看看，或者浏览全部在番作品。"
              actionLabel="查看全部更新"
              actionTo="/updates"
            />
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {shown.map((item) => (
              <UpdateCard key={item.id} item={item} />
            ))}
          </div>
        )}

        {visible < filtered.length && (
          <div className="mt-8 flex justify-center">
            <button
              type="button"
              onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-6 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              <i className="ri-add-line"></i>
              加载更多作品
            </button>
          </div>
        )}
      </div>
    </div>
  );
}